/**
 * Cart Drawer Recommendations
 * 
 * Sistema híbrido que usa metacampos para configurar recomendaciones dinámicas
 * basadas en el primer producto del carrito usando la API de Shopify Recommendations.
 * 
 * Metacampos requeridos (en Stores):
 * - custom.habilitar_recomendaciones_en_carrito (boolean)
 * - custom.tipo_de_recomendacion_en_carrito (text: 'related' o 'complementary')
 * - custom.limite_de_productos_recomendados (number: 2-12)
 */

(function() {
  'use strict';

  // Prevenir múltiples inicializaciones
  if (window.CART_RECOMMENDATIONS_INITIALIZED) {
    console.log('🚫 [Cart Recommendations] Ya inicializado, saltando...');
    return;
  }

  window.CART_RECOMMENDATIONS_INITIALIZED = true;
  console.log('🚀 [Cart Recommendations] Inicializando sistema de recomendaciones...');

  // Configuración desde metacampos (pasada desde Liquid)
  const config = window.CART_RECOMMENDATIONS_CONFIG || {
    enabled: false,
    type: 'related',
    limit: 5
  };

  // Normalizar valores (asegurar tipos correctos)
  config.enabled = config.enabled === true || config.enabled === 'true' || config.enabled === 1;
  config.type = (config.type || 'related').toLowerCase().trim();
  config.limit = parseInt(config.limit) || 5;
  
  // Validar límite (2-12)
  if (config.limit < 2) config.limit = 2;
  if (config.limit > 12) config.limit = 12;

  console.log('🔧 [Cart Recommendations] Configuración:', config);

  /**
   * Obtiene el primer producto del carrito
   */
  async function getFirstCartProduct() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const cart = await response.json();
      
      if (!cart.items || cart.items.length === 0) {
        console.log('ℹ️ [Cart Recommendations] Carrito vacío');
        return null;
      }

      const firstItem = cart.items[0];
      console.log('📦 [Cart Recommendations] Primer producto del carrito:', {
        product_id: firstItem.product_id,
        title: firstItem.product_title
      });

      return {
        product_id: firstItem.product_id,
        handle: firstItem.handle,
        title: firstItem.product_title
      };
    } catch (error) {
      console.error('❌ [Cart Recommendations] Error al obtener carrito:', error);
      return null;
    }
  }

  /**
   * Obtiene productos recomendados desde la API de Shopify
   */
  async function getRecommendedProducts(productId, intent = 'related', limit = 5) {
    try {
      const url = `/recommendations/products.json?product_id=${productId}&limit=${limit}&intent=${intent}`;
      console.log('📡 [Cart Recommendations] Llamando a API:', url);

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ [Cart Recommendations] Productos recomendados recibidos:', data.products?.length || 0);

      return data.products || [];
    } catch (error) {
      console.error('❌ [Cart Recommendations] Error al obtener recomendaciones:', error);
      return [];
    }
  }

  /**
   * Obtiene IDs de productos ya en el carrito para excluirlos
   */
  async function getCartProductIds() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return [];
      
      const cart = await response.json();
      return cart.items.map(item => item.product_id).filter(Boolean);
    } catch (error) {
      console.error('❌ [Cart Recommendations] Error al obtener IDs del carrito:', error);
      return [];
    }
  }

  /**
   * Obtiene datos completos de un producto desde la API
   */
  async function getProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.warn(`⚠️ [Cart Recommendations] Error al obtener datos de ${handle}:`, error);
      return null;
    }
  }

  /**
   * Obtiene el HTML renderizado del precio con descuento usando la API de secciones de Shopify
   * Esto asegura que se incluyan todos los metafields de colección y descuentos
   */
  async function getProductPriceHTML(productHandle) {
    try {
      const url = `/products/${productHandle}?sections=cart-drawer-price-section`;
      console.log(`📡 [Cart Recommendations] Obteniendo precio renderizado para ${productHandle} desde: ${url}`);
      const response = await fetch(url);
      
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No se pudo leer el error');
        console.error(`❌ [Cart Recommendations] HTTP error ${response.status} para ${productHandle}:`, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const sectionKeys = Object.keys(data);
      console.log(`📦 [Cart Recommendations] Respuesta recibida para ${productHandle}, claves:`, sectionKeys);
      
      // La clave puede ser "cart-drawer-price-section" o "cart-drawer-price-section-0", etc.
      const sectionKey = sectionKeys.find(k => k.startsWith('cart-drawer-price-section'));
      const sectionHTML = sectionKey ? data[sectionKey] : data['cart-drawer-price-section'];
      
      if (!sectionHTML || typeof sectionHTML !== 'string') {
        console.warn(`⚠️ [Cart Recommendations] No se encontró sección cart-drawer-price-section en la respuesta para ${productHandle}`);
        return null;
      }
      
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      const priceWrapper = doc.querySelector('.LOP-product-price-wrapper');
      
      if (!priceWrapper) {
        console.warn(`⚠️ [Cart Recommendations] No se encontró .LOP-product-price-wrapper en el HTML para ${productHandle}`);
        console.warn(`📋 [Cart Recommendations] HTML recibido (primeros 500 caracteres):`, sectionHTML.substring(0, 500));
        return null;
      }
      
      // Extraer solo el precio (sin badges adicionales, sin precio sin impuestos, sin cuotas)
      const priceElement = priceWrapper.querySelector('.product-price');
      if (priceElement) {
        const priceHTML = priceElement.outerHTML;
        console.log(`✅ [Cart Recommendations] Precio HTML obtenido para ${productHandle}:`, priceHTML.substring(0, 150) + '...');
        return priceHTML;
      }
      
      // Fallback: usar todo el wrapper si no encontramos .product-price
      console.warn(`⚠️ [Cart Recommendations] No se encontró .product-price en el wrapper para ${productHandle}, usando wrapper completo`);
      return priceWrapper.innerHTML;
    } catch (error) {
      console.error(`❌ [Cart Recommendations] Error al obtener HTML de precio para ${productHandle}:`, error);
      return null;
    }
  }

  /**
   * Formatea dinero en formato argentino
   */
  function formatMoney(cents) {
    const amount = Math.round(cents / 100);
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  /**
   * Genera el HTML para un producto del carrusel
   * @param {Object} product - Datos del producto
   * @param {string} buttonText - Texto del botón
   * @param {string} priceHTML - HTML renderizado del precio (opcional, si no se proporciona se calcula manualmente)
   */
  function generateProductHTML(product, buttonText = 'Añadir al carrito', priceHTML = null) {
    // Manejar imagen
    let imageHTML = '';
    if (product.featured_image) {
      const imageUrl = product.featured_image.startsWith('http') 
        ? product.featured_image 
        : product.featured_image.startsWith('//') 
          ? `https:${product.featured_image}`
          : `${window.location.origin}${product.featured_image}`;
      
      imageHTML = `<img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy" class="lazyload" style="width: 100%; height: 100%; object-fit: contain;">`;
    } else {
      imageHTML = `<div class="thb-placeholder" style="width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; background: #f5f5f5;">
        <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
          <rect width="100" height="100" fill="#f5f5f5"/>
          <text x="50" y="50" text-anchor="middle" fill="#999" font-size="12">Sin imagen</text>
        </svg>
      </div>`;
    }

    // Truncar título
    const title = product.title.length > 24 
      ? product.title.substring(0, 24) + '...' 
      : product.title;

    // Obtener variante disponible (necesario para disponibilidad y precio)
    const variant = product.variants?.find(v => v.available) || product.variants?.[0];

    // Si no se proporciona priceHTML renderizado, calcular manualmente (fallback)
    if (!priceHTML) {
      const originalPriceInCents = variant?.price || product.price || 0;
      
      // Intentar calcular descuento basado en metafields de colección (si están disponibles)
      let hasCollectionDiscount = false;
      let discountPercentage = 0;
      let discountedPriceInCents = originalPriceInCents;
      
      if (product.collections && Array.isArray(product.collections)) {
        for (const collection of product.collections) {
          let collectionDiscount = null;
          
          // Intentar diferentes estructuras de metafields
          if (collection.metafields && collection.metafields.custom && collection.metafields.custom.numero_de_descuento) {
            collectionDiscount = collection.metafields.custom.numero_de_descuento;
          } else if (collection.metafields?.custom?.numero_de_descuento) {
            collectionDiscount = collection.metafields.custom.numero_de_descuento;
          } else if (collection.numero_de_descuento) {
            collectionDiscount = collection.numero_de_descuento;
          }
          
          if (collectionDiscount) {
            discountPercentage = parseInt(collectionDiscount) || 0;
            if (discountPercentage > 0) {
              hasCollectionDiscount = true;
              const discountMultiplier = 100 - discountPercentage;
              discountedPriceInCents = Math.round(originalPriceInCents * discountMultiplier / 100);
              break;
            }
          }
        }
      }
      
      // Fallback: usar compare_at_price si no hay descuento de colección
      const compareAtPriceInCents = variant?.compare_at_price || product.compare_at_price || 0;
      const hasCompareAtDiscount = !hasCollectionDiscount && compareAtPriceInCents > originalPriceInCents && compareAtPriceInCents > 0;
      
      // Determinar precios finales
      let finalPriceInCents = originalPriceInCents;
      let originalPriceToShow = originalPriceInCents;
      
      if (hasCollectionDiscount) {
        finalPriceInCents = discountedPriceInCents;
        originalPriceToShow = originalPriceInCents;
      } else if (hasCompareAtDiscount) {
        finalPriceInCents = originalPriceInCents;
        originalPriceToShow = compareAtPriceInCents;
      }
      
      const hasDiscount = hasCollectionDiscount || hasCompareAtDiscount;
      
      // Formatear precios
      const formattedPrice = formatMoney(finalPriceInCents);
      const formattedComparePrice = hasDiscount ? formatMoney(originalPriceToShow) : '';

      // Generar HTML de precio
      if (hasDiscount) {
        priceHTML = `
          <del class="LOP-cart-drawer--carousel-price-compare">${formattedComparePrice}</del>
          <ins class="LOP-cart-drawer--carousel-price-final">${formattedPrice}</ins>
        `;
      } else {
        priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${formattedPrice}</ins>`;
      }
    }
    
    // Convertir el HTML renderizado al formato del carrito SOLO si viene de la API de secciones
    // (contiene .product-price). Si es nuestro fallback ya tiene las clases del carrito, no tocar.
    if (priceHTML && typeof priceHTML === 'string' && priceHTML.trim() && priceHTML.includes('product-price')) {
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = priceHTML;
        
        // Buscar precio tachado y precio final dentro del elemento .product-price
        const priceElement = tempDiv.querySelector('.product-price');
        if (priceElement) {
          const strikeElement = priceElement.querySelector('.product-price--strike');
          const saleElement = priceElement.querySelector('.product-price--sale');
          const regularElement = priceElement.querySelector('.product-price--regular:not(.product-price--strike)');
          
          if (strikeElement && saleElement) {
            // Hay descuento: convertir a formato del carrito
            const strikeText = strikeElement.textContent.trim();
            const saleText = saleElement.textContent.trim();
            priceHTML = `
              <del class="LOP-cart-drawer--carousel-price-compare">${escapeHtml(strikeText)}</del>
              <ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(saleText)}</ins>
            `;
            console.log(`✅ [Cart Recommendations] Precio con descuento convertido para ${product.handle}: ${strikeText} → ${saleText}`);
          } else if (regularElement) {
            // Sin descuento: solo precio regular
            const regularText = regularElement.textContent.trim();
            priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(regularText)}</ins>`;
            console.log(`ℹ️ [Cart Recommendations] Precio sin descuento para ${product.handle}: ${regularText}`);
          } else if (saleElement && !strikeElement) {
            // Solo precio de venta sin tachado
            const saleText = saleElement.textContent.trim();
            priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(saleText)}</ins>`;
            console.log(`ℹ️ [Cart Recommendations] Precio de venta sin tachado para ${product.handle}: ${saleText}`);
          } else {
            // Si no encontramos elementos específicos, usar el texto completo
            const allText = priceElement.textContent.trim();
            if (allText) {
              priceHTML = `<ins class="LOP-cart-drawer--carousel-price-final">${escapeHtml(allText)}</ins>`;
              console.warn(`⚠️ [Cart Recommendations] No se encontraron elementos de precio específicos para ${product.handle}, usando texto completo: ${allText}`);
            } else {
              // Si no hay texto, usar fallback
              priceHTML = null;
            }
          }
        } else {
          console.warn(`⚠️ [Cart Recommendations] No se encontró elemento .product-price en el HTML renderizado para ${product.handle}, usando fallback`);
          priceHTML = null; // Usar fallback si no se encuentra el elemento
        }
      } catch (error) {
        console.error(`❌ [Cart Recommendations] Error al convertir precio HTML para ${product.handle}:`, error);
        // Usar fallback si hay error
        priceHTML = null;
      }
    }

    // Determinar disponibilidad
    const isAvailable = variant?.available !== false && variant?.id;
    const variantId = variant?.id || product.id;

    return `
      <div class="LOP-cart-drawer--carousel-item">
        <a href="/products/${product.handle}" class="LOP-cart-drawer--carousel-item-link" title="${escapeHtml(title)}">
          <div class="LOP-cart-drawer--carousel-item-image">
            ${imageHTML}
          </div>
          <div class="LOP-cart-drawer--carousel-item-info">
            <span class="LOP-cart-drawer--carousel-item-title">${escapeHtml(title)}</span>
            <div class="LOP-cart-drawer--carousel-item-price">
              ${priceHTML}
            </div>
          </div>
        </a>
        ${isAvailable ? `
          <button class="LOP-cart-drawer--carousel-add-btn" 
            data-product-id="${variantId}"
            data-product-handle="${escapeHtml(product.handle)}"
            type="button">
            <span class="LOP-cart-drawer--carousel-add-btn-text">${escapeHtml(buttonText)}</span>
          </button>
        ` : `
          <button class="LOP-cart-drawer--carousel-add-btn" disabled>
            <span class="LOP-cart-drawer--carousel-add-btn-text">Agotado</span>
          </button>
        `}
      </div>
    `;
  }

  /**
   * Función helper para escapar HTML
   */
  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Actualiza el carrusel con productos recomendados
   */
  async function updateRecommendations() {
    console.log('🔄 [Cart Recommendations] ===== INICIANDO ACTUALIZACIÓN =====');

    // Verificar que esté habilitado
    if (!config.enabled) {
      console.log('ℹ️ [Cart Recommendations] Recomendaciones deshabilitadas en metacampos');
      return;
    }

    // Verificar que el cart drawer esté abierto y visible (sin atributo inert)
    const cartDrawer = document.querySelector('#Cart-Drawer');
    if (!cartDrawer || cartDrawer.hasAttribute('inert')) {
      console.log('ℹ️ [Cart Recommendations] Cart drawer no está abierto (tiene inert)');
      return;
    }

    // Verificar que el cart drawer esté visible (no display: none)
    const drawerComputedStyle = window.getComputedStyle(cartDrawer);
    if (drawerComputedStyle.display === 'none') {
      console.log('⚠️ [Cart Recommendations] Cart drawer está oculto (display: none), esperando...');
      // Esperar hasta que el cart drawer sea visible
      let attempts = 0;
      const checkVisibility = setInterval(() => {
        attempts++;
        const currentDrawer = document.querySelector('#Cart-Drawer');
        if (!currentDrawer || currentDrawer.hasAttribute('inert')) {
          clearInterval(checkVisibility);
          console.error('❌ [Cart Recommendations] Cart drawer se cerró mientras esperábamos');
          return;
        }
        const currentStyle = window.getComputedStyle(currentDrawer);
        if (currentStyle.display !== 'none' || attempts > 20) {
          clearInterval(checkVisibility);
          if (currentStyle.display !== 'none') {
            console.log(`✅ [Cart Recommendations] Cart drawer ahora visible, continuando...`);
            // Continuar con la actualización después de un breve delay
            setTimeout(updateRecommendations, 100);
          } else {
            console.error('❌ [Cart Recommendations] Cart drawer sigue oculto después de esperar');
          }
        }
      }, 100);
      return;
    }

    // Buscar TODAS las secciones del carrusel en modo recomendaciones (puede haber duplicados)
    const allCarouselSections = document.querySelectorAll('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
    
    // Si hay más de una sección, eliminar las duplicadas (dejar solo la primera)
    if (allCarouselSections.length > 1) {
      console.warn(`⚠️ [Cart Recommendations] Se encontraron ${allCarouselSections.length} secciones de carrusel. Eliminando duplicados...`);
      for (let i = 1; i < allCarouselSections.length; i++) {
        const duplicate = allCarouselSections[i];
        console.log(`🗑️ [Cart Recommendations] Eliminando sección duplicada ${i}`);
        duplicate.remove();
      }
    }
    
    // Usar la primera (y única) sección
    const carouselSection = document.querySelector('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
    if (!carouselSection) {
      console.log('ℹ️ [Cart Recommendations] No se encontró sección en modo recomendaciones');
      return;
    }

    const carouselTrack = carouselSection.querySelector('.LOP-cart-drawer--carousel-track');
    if (!carouselTrack) {
      console.log('⚠️ [Cart Recommendations] No se encontró track del carrusel');
      return;
    }

    // Declarar carouselContainer al inicio para que esté disponible en todo el scope
    const carouselContainer = carouselSection.querySelector('.LOP-cart-drawer--carousel-container');

    // Mostrar loading
    carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">Cargando recomendaciones...</div>';

    try {
      // 1. Obtener primer producto del carrito y IDs del carrito en paralelo
      const [firstProduct, cartProductIds] = await Promise.all([
        getFirstCartProduct(),
        getCartProductIds()
      ]);
      
      if (!firstProduct) {
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">Agrega productos al carrito para ver recomendaciones</div>';
        return;
      }

      // 2. Obtener productos recomendados
      const recommendedProducts = await getRecommendedProducts(
        firstProduct.product_id,
        config.type,
        config.limit
      );

      if (recommendedProducts.length === 0) {
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">No hay recomendaciones disponibles</div>';
        return;
      }

      console.log('🚫 [Cart Recommendations] Productos a excluir:', cartProductIds.length);

      // 4. Filtrar productos ya en el carrito
      const filteredProducts = recommendedProducts.filter(product => {
        // Excluir si ya está en el carrito
        if (cartProductIds.includes(product.id)) {
          console.log(`⏭️ [Cart Recommendations] Saltando producto ${product.id} - ya está en el carrito`);
          return false;
        }
        return true;
      });

      console.log(`✅ [Cart Recommendations] Productos después de filtrar carrito: ${filteredProducts.length}`);

      if (filteredProducts.length === 0) {
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">No hay productos recomendados disponibles</div>';
        return;
      }

      // 5. Obtener datos completos de productos (en paralelo, optimizado)
      // Limitar a máximo config.limit + 2 para tener margen
      const productsToFetch = filteredProducts.slice(0, Math.min(config.limit + 2, filteredProducts.length));
      
      // Hacer todas las llamadas en paralelo (más rápido)
      // Usar Promise.allSettled para que si una falla, las demás continúen
      const productDataPromises = productsToFetch.map(product => 
        getProductData(product.handle).catch(error => {
          console.warn(`⚠️ [Cart Recommendations] Error al obtener datos de ${product.handle}:`, error);
          return null; // Retornar null si falla
        })
      );
      const productDataResults = await Promise.allSettled(productDataPromises);
      const productsWithData = productDataResults
        .filter(result => result.status === 'fulfilled' && result.value !== null)
        .map(result => result.value);

      console.log(`✅ [Cart Recommendations] Productos con datos completos: ${productsWithData.length}`);

      if (productsWithData.length === 0) {
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">No hay productos recomendados disponibles</div>';
        return;
      }

      // 6. Ordenar por precio (mayor a menor)
      productsWithData.sort((a, b) => {
        const variantA = a.variants?.find(v => v.available) || a.variants?.[0];
        const variantB = b.variants?.find(v => v.available) || b.variants?.[0];
        const priceA = variantA?.price || a.price || 0;
        const priceB = variantB?.price || b.price || 0;
        return priceB - priceA;
      });

      // Limitar a la cantidad configurada
      const finalProducts = productsWithData.slice(0, config.limit);

      // 7. Obtener precios renderizados con descuentos (metafields de colección) vía API de secciones
      console.log('💰 [Cart Recommendations] Obteniendo precios renderizados con descuentos...');
      const priceHTMLMap = {};
      
      try {
        const priceHTMLPromises = finalProducts.map(product => 
          getProductPriceHTML(product.handle).catch(error => {
            console.warn(`⚠️ [Cart Recommendations] Error al obtener precio para ${product.handle}:`, error);
            return null;
          })
        );
        const priceHTMLResults = await Promise.allSettled(priceHTMLPromises);
        
        finalProducts.forEach((product, index) => {
          const result = priceHTMLResults[index];
          if (result.status === 'fulfilled' && result.value) {
            priceHTMLMap[product.handle] = result.value;
            console.log(`✅ [Cart Recommendations] Precio con descuento obtenido para ${product.handle}`);
          } else {
            priceHTMLMap[product.handle] = null;
            console.warn(`⚠️ [Cart Recommendations] Fallback de precio para ${product.handle}`);
          }
        });
      } catch (error) {
        console.error('❌ [Cart Recommendations] Error al obtener precios renderizados, usando fallback:', error);
        finalProducts.forEach(product => {
          priceHTMLMap[product.handle] = null;
        });
      }

      // 8. Obtener texto del botón desde settings
      // Intentar obtener desde un elemento existente o usar default
      let buttonText = 'Añadir al carrito';
      const existingButton = document.querySelector('.LOP-cart-drawer--carousel-add-btn-text');
      if (existingButton) {
        buttonText = existingButton.textContent.trim() || 'Añadir al carrito';
      } else if (window.LOP_THEME_SETTINGS && window.LOP_THEME_SETTINGS.cartCarouselButtonText) {
        buttonText = window.LOP_THEME_SETTINGS.cartCarouselButtonText;
      }

      // 9. Generar HTML con precios renderizados
      const productsHTML = finalProducts.map(product => {
        try {
          if (!product || !product.handle) {
            console.error(`❌ [Cart Recommendations] Producto inválido:`, product);
            return ''; // Saltar productos inválidos
          }
          const priceHTML = priceHTMLMap[product.handle] || null;
          const html = generateProductHTML(product, buttonText, priceHTML);
          if (!html || html.trim() === '') {
            console.warn(`⚠️ [Cart Recommendations] HTML vacío para ${product.handle}`);
            return ''; // Saltar si el HTML está vacío
          }
          return html;
        } catch (error) {
          console.error(`❌ [Cart Recommendations] Error al generar HTML para ${product?.handle || 'unknown'}:`, error);
          console.error(`❌ [Cart Recommendations] Stack:`, error.stack);
          // Intentar generar HTML básico como fallback
          try {
            return generateProductHTML(product, buttonText, null);
          } catch (fallbackError) {
            console.error(`❌ [Cart Recommendations] Error incluso en fallback para ${product?.handle || 'unknown'}:`, fallbackError);
            return ''; // Saltar este producto completamente
          }
        }
      }).filter(html => html && html.trim() !== '').join(''); // Filtrar HTMLs vacíos

      console.log(`📝 [Cart Recommendations] HTML generado para ${finalProducts.length} productos (${productsHTML.length} caracteres)`);

      // Verificar que tenemos HTML válido
      if (!productsHTML || productsHTML.trim() === '') {
        console.error('❌ [Cart Recommendations] No se generó HTML válido para ningún producto');
        carouselTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #999;">No hay productos recomendados disponibles</div>';
        return;
      }

      // 10. Verificar que el carouselTrack todavía existe (puede haberse regenerado)
      const currentCarouselTrack = carouselSection.querySelector('.LOP-cart-drawer--carousel-track');
      if (!currentCarouselTrack) {
        console.error('❌ [Cart Recommendations] El carouselTrack ya no existe');
        return;
      }

      // 11. Insertar HTML
      try {
        currentCarouselTrack.innerHTML = productsHTML;
      } catch (error) {
        console.error('❌ [Cart Recommendations] Error al insertar HTML:', error);
        throw error; // Re-lanzar para que sea capturado por el catch principal
      }
      console.log(`✅ [Cart Recommendations] HTML insertado en carouselTrack. Elementos hijos: ${currentCarouselTrack.children.length}`);
      
      // Verificar que los items se insertaron correctamente
      const insertedItems = currentCarouselTrack.querySelectorAll('.LOP-cart-drawer--carousel-item');
      console.log(`🔍 [Cart Recommendations] Items insertados verificados: ${insertedItems.length}`);
      
      if (insertedItems.length === 0) {
        console.error('❌ [Cart Recommendations] Los items no se insertaron correctamente');
        return;
      }
      
      // 12. Forzar visibilidad y recálculo de dimensiones
      currentCarouselTrack.style.display = 'flex';
      currentCarouselTrack.style.visibility = 'visible';
      currentCarouselTrack.style.opacity = '1';
      
      // Asegurar que la sección y el contenedor estén visibles
      carouselSection.style.display = 'block';
      carouselSection.style.visibility = 'visible';
      carouselSection.style.opacity = '1';
      
      if (carouselContainer) {
        carouselContainer.style.display = 'block';
        carouselContainer.style.visibility = 'visible';
        carouselContainer.style.opacity = '1';
      }
      
      // Forzar recálculo de layout
      void currentCarouselTrack.offsetWidth;
      void carouselSection.offsetWidth;
      if (carouselContainer) {
        void carouselContainer.offsetWidth;
      }
      
      // 13. Inicializar navegación del carrusel
      const prevButton = carouselSection.querySelector('.LOP-cart-drawer--carousel-prev');
      const nextButton = carouselSection.querySelector('.LOP-cart-drawer--carousel-next');
      
      // Función para actualizar el estado de los botones
      const updateNavButtons = () => {
        if (!carouselContainer) return;
        const scrollLeft = carouselContainer.scrollLeft;
        const scrollWidth = carouselContainer.scrollWidth;
        const clientWidth = carouselContainer.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        
        if (prevButton) prevButton.disabled = scrollLeft <= 0;
        if (nextButton) nextButton.disabled = scrollLeft >= maxScroll - 1;
      };
      
      // Configurar botones de navegación
      if (prevButton) {
        prevButton.addEventListener('click', () => {
          if (!carouselContainer) return;
          const itemWidth = insertedItems[0]?.offsetWidth || 177;
          carouselContainer.scrollBy({ left: -itemWidth * 3, behavior: 'smooth' });
        });
      }
      
      if (nextButton) {
        nextButton.addEventListener('click', () => {
          if (!carouselContainer) return;
          const itemWidth = insertedItems[0]?.offsetWidth || 177;
          carouselContainer.scrollBy({ left: itemWidth * 3, behavior: 'smooth' });
        });
      }
      
      // Actualizar botones cuando se hace scroll
      if (carouselContainer) {
        carouselContainer.addEventListener('scroll', updateNavButtons);
        updateNavButtons(); // Estado inicial
      }
      
      // 14. Inicializar event listeners para botones "Añadir al carrito"
      initCarouselAddToCartButtons();

      console.log('✅ [Cart Recommendations] ===== ACTUALIZACIÓN COMPLETADA =====');
    } catch (error) {
      console.error('❌ [Cart Recommendations] Error al actualizar recomendaciones:', error);
      console.error('❌ [Cart Recommendations] Stack trace:', error.stack);
      console.error('❌ [Cart Recommendations] Error name:', error.name);
      console.error('❌ [Cart Recommendations] Error message:', error.message);
      
      // Verificar que carouselTrack todavía existe antes de mostrar el error
      const errorTrack = carouselSection?.querySelector('.LOP-cart-drawer--carousel-track');
      if (errorTrack) {
        errorTrack.innerHTML = '<div class="LOP-cart-drawer--carousel-loading" style="padding: 20px; text-align: center; color: #d32f2f;">Error al cargar recomendaciones</div>';
      } else {
        console.error('❌ [Cart Recommendations] No se pudo mostrar el error porque carouselTrack no existe');
      }
    }
  }

  /**
   * Inicializa event listeners para botones "Añadir al carrito" del carrusel
   * Usa event delegation a nivel de documento para que funcione siempre
   */
  let carouselAddToCartListenerAdded = false;
  
  function initCarouselAddToCartButtons() {
    // Solo agregar el listener una vez
    if (carouselAddToCartListenerAdded) {
      return;
    }
    
    console.log('🛒 [Cart Recommendations] Configurando event delegation para botones de agregar al carrito...');
    
    // Usar event delegation a nivel de documento
    document.addEventListener('click', async function(e) {
      const addBtn = e.target.closest('.LOP-cart-drawer--carousel-add-btn');
      if (!addBtn || addBtn.disabled) return;
      
      // Verificar que el botón esté dentro del carrusel de recomendaciones
      const carouselSection = addBtn.closest('.LOP-cart-drawer--carousel-section[data-recommendations-mode="true"]');
      if (!carouselSection) return;
      
      e.preventDefault();
      e.stopPropagation();
      
      const productId = addBtn.dataset.productId;
      const productHandle = addBtn.dataset.productHandle;
      
      if (!productId) {
        console.error('❌ [Cart Recommendations] No se encontró ID del producto');
        return;
      }
      
      console.log(`🛒 [Cart Recommendations] Agregando producto del carrusel: ${productId} (${productHandle})`);
      
      // Deshabilitar botón temporalmente
      const originalText = addBtn.querySelector('.LOP-cart-drawer--carousel-add-btn-text')?.textContent || 'Añadir al carrito';
      addBtn.disabled = true;
      const textSpan = addBtn.querySelector('.LOP-cart-drawer--carousel-add-btn-text');
      if (textSpan) {
        textSpan.textContent = 'Agregando...';
      }
      
      try {
        // Agregar producto al carrito
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: productId,
            quantity: 1
          })
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('✅ [Cart Recommendations] Producto agregado desde carrusel:', data);
        
        // Disparar evento para que el cart drawer se actualice
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true
        }));
        
        // También disparar evento cart:item-added si existe
        if (window.dispatchCustomEvent) {
          window.dispatchCustomEvent('cart:item-added', {
            product: data
          });
        } else {
          document.documentElement.dispatchEvent(new CustomEvent('cart:item-added', {
            bubbles: true,
            detail: { product: data }
          }));
        }
        
        // Actualizar recomendaciones después de agregar (excluirá el nuevo producto)
        setTimeout(() => {
          if (window.LOP && window.LOP.triggerRecommendationsUpdate) {
            window.LOP.triggerRecommendationsUpdate();
          }
        }, 500);
        
      } catch (error) {
        console.error('❌ [Cart Recommendations] Error al agregar producto:', error);
        alert('Error al agregar el producto al carrito. Por favor, intenta de nuevo.');
      } finally {
        // Restaurar botón
        addBtn.disabled = false;
        if (textSpan) {
          textSpan.textContent = originalText;
        }
      }
    });
    
    carouselAddToCartListenerAdded = true;
    console.log('✅ [Cart Recommendations] Event delegation configurado para botones de agregar al carrito');
  }

  /**
   * Inicializa el sistema de recomendaciones
   */
  function initialize() {
    console.log('🔧 [Cart Recommendations] Configurando listeners...');

    // Solo inicializar si está habilitado
    if (!config.enabled) {
      console.log('ℹ️ [Cart Recommendations] Recomendaciones deshabilitadas, no inicializando');
      return;
    }

    let isUpdating = false;
    let lastUpdateTime = 0;
    const UPDATE_DEBOUNCE = 300;

    // Función para actualizar con debounce
    function triggerUpdate() {
      const now = Date.now();
      if (isUpdating || (now - lastUpdateTime < UPDATE_DEBOUNCE)) {
        return;
      }
      lastUpdateTime = now;
      isUpdating = true;

      updateRecommendations().finally(() => {
        isUpdating = false;
      });
    }

    // Estrategia 1: Escuchar evento cart-drawer:open del custom element
    document.addEventListener('cart-drawer:open', () => {
      console.log('🔄 [Cart Recommendations] Cart drawer abierto (evento), actualizando recomendaciones...');
      setTimeout(triggerUpdate, 400);
    });
    
    // También interceptar el método open() del custom element si existe
    const cartDrawerElement = document.getElementById('Cart-Drawer');
    if (cartDrawerElement && typeof cartDrawerElement.open === 'function') {
      const originalOpen = cartDrawerElement.open.bind(cartDrawerElement);
      cartDrawerElement.open = function() {
        const result = originalOpen.apply(this, arguments);
        setTimeout(() => {
          console.log('🔄 [Cart Recommendations] Cart drawer abierto (método), actualizando recomendaciones...');
          triggerUpdate();
        }, 400);
        return result;
      };
    }

    // Estrategia 2: MutationObserver para detectar cuando se remueve el atributo 'inert'
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'inert') {
          const target = mutation.target;
          if (target.id === 'Cart-Drawer' && !target.hasAttribute('inert')) {
            console.log('🎯 [Cart Recommendations] Cart drawer abierto (inert removido)');
            setTimeout(triggerUpdate, 400);
          }
        }

        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              const cartDrawer = node.id === 'Cart-Drawer' 
                ? node 
                : node.querySelector?.('#Cart-Drawer');

              if (cartDrawer && !cartDrawer.hasAttribute('inert')) {
                console.log('🎯 [Cart Recommendations] Cart drawer detectado en DOM (sin inert)');
                triggerUpdate();
              }

              if (node.classList?.contains('LOP-cart-drawer--carousel-section') && 
                  node.dataset.recommendationsMode === 'true') {
                console.log('🎯 [Cart Recommendations] Sección de recomendaciones detectada');
                triggerUpdate();
              }
            }
          });
        }
      });
    });

    if (document.body) {
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Estrategia 3: Escuchar eventos de actualización del carrito
    document.addEventListener('line-item:change:end', () => {
      console.log('🔄 [Cart Recommendations] Carrito actualizado, actualizando recomendaciones...');
      setTimeout(triggerUpdate, 400);
    });
    
    document.addEventListener('cart:refresh', () => {
      console.log('🔄 [Cart Recommendations] Carrito refrescado, actualizando recomendaciones...');
      setTimeout(triggerUpdate, 400);
    });

    // Inicializar event listeners para botones de agregar al carrito
    initCarouselAddToCartButtons();
    
    // Exponer función globalmente
    window.LOP = window.LOP || {};
    window.LOP.updateRecommendations = updateRecommendations;
    window.LOP.triggerRecommendationsUpdate = triggerUpdate;

    // Función de test
    window.testRecommendations = function() {
      console.log('🧪 [Cart Recommendations] TEST MANUAL - Forzando actualización...');
      updateRecommendations();
    };

    console.log('✅ [Cart Recommendations] Sistema inicializado');
  }

  // Inicializar cuando el DOM esté listo
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

})();
